import { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import WorkerLayout from '../../components/WorkerLayout';
import { useTheme } from '../../components/AdminLayout';
import DashboardStatCard from '../../components/dashboard/DashboardStatCard';

function WorkerHomeContent() {
  const { T, S } = useTheme();
  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    Promise.all([
      api.getWorkerToday().catch(() => []),
      api.getWorkerProfile().catch(() => null),
    ]).then(([b, p]) => {
      setBookings(b || []);
      setProfile(p);
    }).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (bookingId, newStatus) => {
    setActionLoading(bookingId);
    try {
      await api.updateWorkerBookingStatus(bookingId, newStatus);
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const completed = bookings.filter(b => b.status === 'completed').length;
  const inProgress = bookings.filter(b => b.status === 'in_progress').length;
  const upcoming = bookings.filter(b => b.status === 'confirmed').length;
  const accent = T.orange;

  return (
    <WorkerLayout>
      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={S.pageTitle}>
          {profile ? `Welcome, ${profile.name}` : "Today's Work"}
        </h1>
        <div style={{ width: '40px', height: '3px', background: accent, borderRadius: '2px', marginTop: '0.3rem' }} />
        {profile?.branch_name && (
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: T.muted, marginTop: '0.35rem' }}>
            {profile.branch_name}{profile.shop_name ? ` — ${profile.shop_name}` : ''}
          </p>
        )}
      </div>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.75rem' }}>
        <DashboardStatCard
          icon="📋"
          iconColor={accent}
          label="Total Today"
          value={bookings.length}
        />
        <DashboardStatCard
          icon="⏳"
          iconColor="#f59e0b"
          label="Upcoming"
          value={upcoming}
        />
        <DashboardStatCard
          icon="🔧"
          iconColor="#6366f1"
          label="In Progress"
          value={inProgress}
        />
        <DashboardStatCard
          icon="✓"
          iconColor="#16a34a"
          label="Completed"
          value={completed}
        />
      </div>

      {/* Bookings List */}
      <div style={S.card}>
        <h2 style={{
          fontFamily: "'Sora', sans-serif", fontSize: '0.95rem', fontWeight: 600,
          color: T.white, marginBottom: '1rem',
        }}>
          Today's Bookings
        </h2>

        {loading ? (
          <p style={{ color: T.muted }}>Loading...</p>
        ) : bookings.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '3rem 1rem',
            color: T.muted, fontSize: '0.95rem',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎉</div>
            No bookings for today. Enjoy your free time!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {bookings.map(b => {
              const statusColors = {
                confirmed: { bg: '#fef3c7', color: '#92400e', label: 'Upcoming' },
                in_progress: { bg: '#dbeafe', color: '#1d4ed8', label: 'In Progress' },
                completed: { bg: '#dcfce7', color: '#15803d', label: 'Completed' },
              };
              const sc = statusColors[b.status] || statusColors.confirmed;

              return (
                <div key={b.id} style={{
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  padding: '1rem 1.25rem',
                  border: `1px solid ${T.border}`,
                  borderRadius: '12px',
                  background: '#fff',
                  transition: 'border-color 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = `${accent}50`}
                  onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                >
                  {/* Time */}
                  <div style={{
                    fontFamily: "'Sora', sans-serif", fontSize: '1rem', fontWeight: 700,
                    color: accent, minWidth: '60px', flexShrink: 0,
                  }}>
                    {b.booking_time?.slice(0, 5) || '--:--'}
                  </div>

                  {/* Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 600,
                      color: T.white, marginBottom: '0.2rem',
                    }}>
                      {b.client_name || 'Walk-in'}
                    </div>
                    <div style={{
                      fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem',
                      color: T.muted,
                    }}>
                      {b.service_name || 'Service'}{b.seat_name ? ` — Seat: ${b.seat_name}` : ''}
                      {b.duration_minutes ? ` — ${b.duration_minutes} min` : ''}
                    </div>
                  </div>

                  {/* Status pill */}
                  <span style={{
                    padding: '0.2rem 0.6rem',
                    borderRadius: '999px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                    background: sc.bg,
                    color: sc.color,
                    flexShrink: 0,
                  }}>
                    {sc.label}
                  </span>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                    {b.status === 'confirmed' && (
                      <button
                        onClick={() => updateStatus(b.id, 'in_progress')}
                        disabled={actionLoading === b.id}
                        style={{
                          padding: '0.35rem 0.8rem',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#6366f1',
                          color: '#fff',
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          opacity: actionLoading === b.id ? 0.6 : 1,
                        }}
                      >
                        Start
                      </button>
                    )}
                    {b.status === 'in_progress' && (
                      <button
                        onClick={() => updateStatus(b.id, 'completed')}
                        disabled={actionLoading === b.id}
                        style={{
                          padding: '0.35rem 0.8rem',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#16a34a',
                          color: '#fff',
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          opacity: actionLoading === b.id ? 0.6 : 1,
                        }}
                      >
                        Complete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </WorkerLayout>
  );
}

export default function WorkerHome() {
  return <ProtectedRoute requireWorker><WorkerHomeContent /></ProtectedRoute>;
}
